// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, ButtonBase, IconButton, Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import CollapseArrow from 'Components/CollapseArrow';
import BulkDownloadRow from '../BulkDownloadRow';
import stylesDef from './styles';

interface ComponentProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  // The section's downloadable files, for its Download All.
  files: IDownloadableFileRef[];
  // Pane-specific chrome on top of the shared section header styles.
  sx?: SxProps<Theme>;
  // Pane-specific arrow button geometry, so the arrow shares the column of the pane's own fold control.
  arrowSx?: SxProps<Theme>;
}

// A file-kind section header that folds its list (AUT-6669): the title and the arrow toggle it,
// the count and Download All keep their own click. The arrow is the keyboard and screen-reader
// control; the title is a mouse shortcut, so it stays out of the tab order.
export const CollapsibleSectionHeader: React.FC<ComponentProps> = ({ title, open, onToggle, files, sx, arrowSx }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  return (
    <Box sx={[styles.sectionHeader, ...(Array.isArray(sx) ? sx : [sx])]}>
      <ButtonBase sx={styles.titleButton} onClick={onToggle} tabIndex={-1} aria-hidden>
        <Typography sx={styles.sectionTitle}>{title}</Typography>
      </ButtonBase>
      <BulkDownloadRow files={files} align="end" />
      <IconButton
        size="small"
        sx={[styles.arrowButton, ...(Array.isArray(arrowSx) ? arrowSx : [arrowSx])]}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <CollapseArrow open={open} />
      </IconButton>
    </Box>
  );
};

CollapsibleSectionHeader.defaultProps = {
  sx: undefined,
  arrowSx: undefined,
};

export default CollapsibleSectionHeader;
